/**
 * Recipe schema rules — the single source of truth for what a Content Studio
 * recipe accepts, which of its inputs apply to a given product, and when it is
 * complete enough to generate.
 *
 * These rules used to live inside RecipeNode's render body. They were lifted
 * out because more than one consumer needs them: the node itself, and the
 * planner, which has to validate whatever a language model proposes before it
 * can become a canvas node. Two copies of these rules would drift within a
 * release.
 *
 * Deliberately free of React, i18n and MUI so the same file can be ported to
 * pms-api-v2 when the planner moves server-side. Mode-tab labels are returned
 * as i18n keys for the caller to resolve.
 */
import { resolveRecipeFlags, resolveSubmitSlug } from './context';
import { resolveVisibleInputs } from './inputs';
import { resolveMissingInputs } from './validation';
import { RecipeSchemaContext, ResolvedRecipeSchema } from './types';

export * from './types';
export { resolveRecipeFlags, resolveSubmitSlug, fillSubmitDefaults, registerModeTabs, surfaceShotName } from './context';
export { resolveVisibleInputs } from './inputs';
export { resolveMissingInputs, isHiddenForValidation, isEmptyValue } from './validation';
export { coerceRecipeInputs } from './coerce';
export { routesThroughVisualizer, needsWallPicker, keepsThePhotograph } from './visualizer-route';
export type { CoercionReport } from './coerce';

/**
 * Resolve everything about a recipe in one pass — flags, visible inputs,
 * submit slug and validation. Callers that need only one part can import the
 * individual resolvers instead.
 */
export function resolveRecipeSchema(ctx: RecipeSchemaContext): ResolvedRecipeSchema {
    const flags = resolveRecipeFlags(ctx);
    const inputs = resolveVisibleInputs(ctx);
    const missing = resolveMissingInputs(ctx, flags, inputs);

    return {
        flags,
        inputs,
        submitSlug: resolveSubmitSlug(ctx),
        missing,
        isValid: missing.length === 0,
    };
}
